import React from 'react';
import SvgCompass from './SvgCompass';
import DisplaySection from './DisplaySection';


/**
 * VehicleDataDisplay Component
 *
 * A compact React component to display vehicle speed in MPH and heading
 * in both cardinal text (N, NE, E, etc.) and angle.
 *
 * @param {object} props - The component props.
 * @param {number} props.speedValue - The raw speed value from the data.
 * @param {string} props.speedUnit - The unit of the speed (e.g., "kilometer per hour").
 * @param {number} props.headingAngle - The heading angle in degrees (0-360, where 0 is North).
 */
export default function VehicleDataDisplay({ activeState }) {
  const speedValue = activeState?.speed?.value || 0;
  const speedUnit = activeState?.speed?.unit || 'kilometer per hour';
  const headingAngle = activeState?.heading?.toFixed(0) || 0; // Default to 0

  /**
   * Converts speed from kilometers per hour to miles per hour.
   * @param {number} kmph - Speed in kilometers per hour.
   * @returns {number} Speed in miles per hour.
   */
  const convertKmPerHourToMph = (kmph) => {
    // Conversion factor: 1 kilometer = 0.621371 miles
    return kmph * 0.621371;
  };

  /**
   * Determines the cardinal direction (N, NE, E, SE, S, SW, W, NW) from an angle.
   *
   * @param {number} angle - The angle in degrees (0-360, where 0 is North).
   * @returns {string} The cardinal direction.
   */
  const getCardinalDirection = (angle) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
    // Normalize angle to be between 0 and 360 degrees.
    // Adding 360 and then modulo 360 handles negative angles correctly.
    const normalizedAngle = (angle % 360 + 360) % 360;

    // Each direction covers 45 degrees (360 / 8).
    // We add 22.5 (half of 45) to shift the ranges so that N is centered around 0,
    // NE around 45, etc., allowing proper rounding.
    const index = Math.round(normalizedAngle / 45);

    // Use modulo 8 to handle the wrap-around for North (index 8 becomes 0).
    return directions[index % 8];
  };

  // Convert the speed to miles per hour, formatted to two decimal places.
  const speedInMph = (speedUnit === 'kilometer per hour' ? convertKmPerHourToMph(speedValue) : speedValue).toFixed(0);

  // Get the cardinal direction from the heading angle.
  const cardinalDirection = getCardinalDirection(headingAngle);

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        padding: '0rem',
        maxWidth: '300px',
        width: '100%',
        fontFamily: 'Inter, sans-serif',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        <DisplaySection
          color="blue"
          label="Speed"
          value={speedInMph}
          unit="mph"
        />
        <DisplaySection
          color="green"
          label="Heading"
          value={cardinalDirection}
          unit={`(${headingAngle}°)`}
          graphic={<SvgCompass angle={headingAngle} />}
        />
      </div>
    </div>
  );
}